import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  FileSpreadsheet, 
  FileCode, 
  FileStack,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  ChevronDown,
  Download,
  ShieldCheck,
  Zap,
  Lock,
  Loader2,
  Upload,
  LayoutGrid,
  ArrowLeft,
  Activity,
  KeyRound,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { FileUpload } from '@/components/FileUpload';
import { ConverterData } from '@/data/convertersData';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, Link } from 'react-router-dom';
import PremiumBackground from './PremiumBackground';
import PageHeader from './PageHeader';
import { Footer } from './Footer';
import * as pdfjsLib from 'pdfjs-dist';
import * as XLSX from 'xlsx';
import { PDFDocument } from 'pdf-lib';

const CSV_MIME = 'text/csv;charset=utf-8';
const PDF_MIME = 'application/pdf';

/** Real Excel → CSV (UTF-8 with BOM, compatible with Excel & LibreOffice). */
async function convertExcelToCsv(file: File): Promise<Blob> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const firstSheetName = wb.SheetNames[0];
  if (!firstSheetName) throw new Error('EMPTY_WORKBOOK');
  const ws = wb.Sheets[firstSheetName];
  const csv = XLSX.utils.sheet_to_csv(ws, { FS: ',', blankrows: false });
  if (!csv.trim()) throw new Error('EMPTY_WORKBOOK');
  // BOM garante leitura UTF-8 correta no Excel
  const bom = '\uFEFF';
  return new Blob([bom + csv], { type: CSV_MIME });
}

export type CompressionLevel = 'light' | 'medium' | 'extreme';

const COMPRESSION_PRESETS: Record<CompressionLevel, { dpi: number; quality: number }> = {
  light:   { dpi: 200, quality: 0.85 },
  medium:  { dpi: 120, quality: 0.70 },
  extreme: { dpi: 90,  quality: 0.50 },
};

/**
 * Real PDF compression: rasterize each page via pdf.js at target DPI,
 * JPEG-encode via canvas, then rebuild a new PDF with pdf-lib.
 * Falls back to the original (re-saved) if the result is larger.
 */
async function compressPdf(
  file: File,
  level: CompressionLevel = 'medium',
): Promise<{ blob: Blob; originalSize: number; finalSize: number; ratio: number }> {
  const buf = await file.arrayBuffer();
  const originalSize = buf.byteLength;
  const { dpi, quality } = COMPRESSION_PRESETS[level];

  // Load with pdf.js to rasterize
  const srcDoc = await pdfjsLib.getDocument({ data: new Uint8Array(buf.slice(0)) }).promise;
  const outDoc = await PDFDocument.create();
  // Strip metadata
  outDoc.setTitle(''); outDoc.setAuthor(''); outDoc.setSubject('');
  outDoc.setKeywords([]); outDoc.setProducer(''); outDoc.setCreator('');

  const scale = dpi / 72; // pdf.js viewport unit = 72 DPI

  for (let i = 1; i <= srcDoc.numPages; i++) {
    const page = await srcDoc.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('CANVAS_UNAVAILABLE');
    // White background so JPEG (no alpha) looks correct
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport, canvas }).promise;

    const jpegBlob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('JPEG_ENCODE_FAILED'))),
        'image/jpeg',
        quality,
      );
    });
    const jpegBytes = new Uint8Array(await jpegBlob.arrayBuffer());
    const img = await outDoc.embedJpg(jpegBytes);

    // Restore page to the original PDF size (points)
    const origViewport = page.getViewport({ scale: 1 });
    const pageW = origViewport.width;
    const pageH = origViewport.height;
    const newPage = outDoc.addPage([pageW, pageH]);
    newPage.drawImage(img, { x: 0, y: 0, width: pageW, height: pageH });
  }

  await srcDoc.cleanup();

  const out = await outDoc.save({ useObjectStreams: true, addDefaultPage: false });
  const head = new TextDecoder().decode(out.slice(0, 5));
  if (head !== '%PDF-') throw new Error('INVALID_PDF_OUTPUT');
  let finalBytes: Uint8Array = out;

  // Fallback: if rasterized result is larger than original, just re-save original
  if (finalBytes.byteLength >= originalSize) {
    const reDoc = await PDFDocument.load(buf, { ignoreEncryption: false });
    reDoc.setTitle(''); reDoc.setAuthor(''); reDoc.setSubject('');
    reDoc.setKeywords([]); reDoc.setProducer(''); reDoc.setCreator('');
    const resaved = await reDoc.save({ useObjectStreams: true, addDefaultPage: false });
    if (resaved.byteLength < finalBytes.byteLength) finalBytes = resaved;
  }

  const blob = new Blob([finalBytes as BlobPart], { type: PDF_MIME });
  const finalSize = blob.size;
  const ratio = originalSize > 0 ? Math.max(0, (originalSize - finalSize) / originalSize) : 0;
  return { blob, originalSize, finalSize, ratio };
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

type PdfTextItem = { str: string; transform: number[]; width?: number };

function isPdfTextItem(item: unknown): item is PdfTextItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'str' in item &&
    'transform' in item &&
    Array.isArray((item as { transform?: unknown }).transform)
  );
}

const pdfjsWorkerConfig = pdfjsLib as typeof pdfjsLib & {
  GlobalWorkerOptions: { workerSrc: string };
  version: string;
};

// Configure pdfjs worker once (shared with PdfPasswordRemoverPage)
try {
  pdfjsWorkerConfig.GlobalWorkerOptions.workerSrc =
    `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsWorkerConfig.version}/build/pdf.worker.min.mjs`;
} catch { /* no-op */ }

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Extract rows from a PDF using X-coordinate column mapping.
 * Detects a header row containing known column labels, builds column
 * boundaries from each header's X position, then assigns every text item
 * on subsequent rows to a column by its X coordinate — never by spaces.
 */
const REPORT_HEADERS = [
  'Data', 'Paciente', 'Médico', 'Exame', 'Modal.', 'Espec.',
  'Categ.', 'Prior.', 'Accession', 'Origem', 'Qtd', 'Valor Total',
];

const DEBUG_PARSER =
  typeof localStorage !== 'undefined' &&
  localStorage.getItem('debugPdfParser') === '1';

const Y_TOL = 3;

type Item = { x: number; xEnd: number; y: number; str: string };

function normalizeHeader(s: string): string {
  return s
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[\s.]/g, '');
}
const HEADER_KEYS = REPORT_HEADERS.map(normalizeHeader);
const REPORT_COLUMN_COUNT = REPORT_HEADERS.length;
const ORIGIN_IDX = 9;
const QTD_IDX = 10;
const TOTAL_IDX = 11;

function detectHeaderRow(rows: Item[][]): {
  index: number;
  anchors: { name: string; index: number; x: number }[];
} | null {
  for (let i = 0; i < Math.min(rows.length, 30); i++) {
    const row = rows[i];
    // Combine consecutive items to handle "Valor Total" split across items
    const found: { name: string; index: number; x: number }[] = [];
    for (let j = 0; j < row.length; j++) {
      const single = normalizeHeader(row[j].str);
      const pair = j + 1 < row.length
        ? normalizeHeader(row[j].str + row[j + 1].str) : '';
      const hitPair = HEADER_KEYS.indexOf(pair);
      if (hitPair >= 0) {
        found.push({ name: REPORT_HEADERS[hitPair], index: hitPair, x: row[j].x });
        j++; continue;
      }
      const hit = HEADER_KEYS.indexOf(single);
      if (hit >= 0) found.push({ name: REPORT_HEADERS[hit], index: hit, x: row[j].x });
    }
    if (found.length >= 5) {
      found.sort((a, b) => a.x - b.x);
      return { index: i, anchors: found };
    }
  }
  return null;
}

export type ExtractedPdf = {
  rows: string[][]; // includes single header row at index 0 when detected
  pageCount: number;
  hasHeader: boolean;
};

async function extractPdfRows(
  file: File,
  password?: string,
): Promise<ExtractedPdf> {
  const buf = await file.arrayBuffer();
  const doc = await pdfjsLib.getDocument({
    data: new Uint8Array(buf),
    password,
  }).promise;
  console.log('[pdf→xlsx] PDF recebido:', file.name, 'páginas:', doc.numPages);

  const allRows: string[][] = [];
  let headerWritten = false;
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();

    const items: Item[] = [];
    for (const raw of content.items) {
      if (!isPdfTextItem(raw)) continue;
      const it = raw;
      const str = (it.str ?? '').toString();
      if (!str.trim()) continue;
      const tr = it.transform as number[];
      const width = (it.width as number) ?? 0;
      items.push({ x: tr[4], xEnd: tr[4] + width, y: tr[5], str });
    }

    const ySorted = [...items].sort((a, b) => b.y - a.y);
    const itemRows: Item[][] = [];
    for (const it of ySorted) {
      const last = itemRows[itemRows.length - 1];
      if (last && Math.abs(last[0].y - it.y) <= Y_TOL) last.push(it);
      else itemRows.push([it]);
    }
    for (const r of itemRows) r.sort((a, b) => a.x - b.x);

    const header = detectHeaderRow(itemRows);
    let pageRows: string[][] = [];

    if (header) {
      const anchors = header.anchors;
      const bounds: number[] = [];
      for (let i = 0; i < anchors.length - 1; i++) {
        bounds.push((anchors[i].x + anchors[i + 1].x) / 2);
      }
      const colOf = (x: number) => {
        for (let i = 0; i < bounds.length; i++) if (x < bounds[i]) return i;
        return Math.min(anchors.length - 1, REPORT_COLUMN_COUNT - 1);
      };
      if (!headerWritten) {
        allRows.push(REPORT_HEADERS);
        headerWritten = true;
      }
      for (let i = header.index + 1; i < itemRows.length; i++) {
        const cells = Array(REPORT_COLUMN_COUNT).fill('');
        for (const it of itemRows[i]) {
          const anchorCol = colOf(it.x);
          const c = anchors[anchorCol]?.index ?? anchorCol;
          cells[c] = cells[c] ? `${cells[c]} ${it.str.trim()}` : it.str.trim();
          if (DEBUG_PARSER) {
            console.log('[debug]', { text: it.str, x: it.x.toFixed(1), col: REPORT_HEADERS[c] });
          }
        }
        if (cells.some((c) => c)) pageRows.push(cells);
      }
      console.log(`[pdf→xlsx] página ${p}: header detectado, ${pageRows.length} linhas`);
    } else {
      pageRows = itemRows.map((r) => r.map((i) => i.str.trim())).filter((r) => r.length);
      console.log(`[pdf→xlsx] página ${p}: sem header, ${pageRows.length} linhas (fallback)`);
    }

    allRows.push(...pageRows);
  }
  await doc.cleanup();
  return { rows: allRows, pageCount: doc.numPages, hasHeader: headerWritten };
}

/**
 * Normalize report rows where the "Prior." cell contains "URGÊNCIA <num>"
 * merged with the accession number — split it and realign trailing columns.
 * Expected 12-column layout:
 * Data | Paciente | Médico | Exame | Modal. | Espec. | Categ. | Prior. |
 * Accession | Origem | Qtd | Valor Total
 */
const PRIORITY_RE = /^(URG[ÊE]NCIA|ROTINA|ELETIVO|URGENTE)\s+(\d{3,})$/i;
const MONEY_TOKEN_RE = /R\$\s?\d{1,3}(?:\.\d{3})*,\d{2}|R\$\s?\d+,\d{2}/;
const BARE_MONEY_TOKEN_RE = /\d{1,3}(?:\.\d{3})*,\d{2}|\d+,\d{2}/;
const MONEY_RE = new RegExp(`^(?:${MONEY_TOKEN_RE.source}|${BARE_MONEY_TOKEN_RE.source})$`);
const QTD_WITH_MONEY_RE = new RegExp(`^(\\d+)\\s+(${MONEY_TOKEN_RE.source}|${BARE_MONEY_TOKEN_RE.source})$`);
const INTEGER_RE = /^\d+$/;
const FOOTER_RE = /^(p[aá]gina\s+\d+\s+de\s+\d+|relat[oó]rio\s+gerado\s+automaticamente.*|sistema\s+one\.a\.i.*|todos\s+os\s+direitos\s+reservados.*)$/i;

function cleanCell(cell: unknown): string {
  return (cell ?? '').toString().replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

function isMoneyCell(cell: string): boolean {
  return MONEY_RE.test(cleanCell(cell));
}

function isOriginCell(cell: string): boolean {
  const value = cleanCell(cell);
  return Boolean(value) && !INTEGER_RE.test(value) && !isMoneyCell(value);
}

function isFooterRow(row: string[]): boolean {
  const joined = row.map((c) => (c ?? '').trim()).filter(Boolean).join(' ').trim();
  return FOOTER_RE.test(joined);
}

function normalizeRow(row: string[]): string[] | null {
  const original = row.map(cleanCell);
  if (!original.some(Boolean)) return null;
  if (isFooterRow(original)) {
    console.log('[pdf→xlsx] rodapé removido:', { original });
    return null;
  }

  if (original.map(normalizeHeader).join('|') === HEADER_KEYS.join('|')) {
    return REPORT_HEADERS;
  }

  let work = [...original];
  const moved: string[] = [];
  let removedExtras: string[] = [];
  const prior = (work[7] ?? '').trim().match(PRIORITY_RE);
  if (prior) {
    work = [
      ...work.slice(0, 7),
      prior[1].toUpperCase().replace('URGENCIA', 'URGÊNCIA'),
      prior[2],
      ...work.slice(8),
    ];
    moved.push(`Prior./Accession separados: ${original[7]} → ${work[7]} | ${work[8]}`);
  }

  while (work.length < REPORT_COLUMN_COUNT) work.push('');

  const moneyAfterQtdIndex = work.findIndex((cell, index) => index > QTD_IDX && isMoneyCell(cell));
  const moneyAfterQtd = moneyAfterQtdIndex >= 0 ? work[moneyAfterQtdIndex].trim() : '';
  const origem = cleanCell(work[ORIGIN_IDX]);
  const qtd = cleanCell(work[QTD_IDX]);
  const total = cleanCell(work[TOTAL_IDX]);
  const qtdWithMoney = qtd.match(QTD_WITH_MONEY_RE);

  if (qtdWithMoney && !total) {
    work[QTD_IDX] = qtdWithMoney[1];
    work[TOTAL_IDX] = qtdWithMoney[2];
    moved.push(`Qtd/Valor Total separados: ${qtd} → ${work[QTD_IDX]} | ${work[TOTAL_IDX]}`);
  } else if (!origem && isOriginCell(qtd) && INTEGER_RE.test(total) && moneyAfterQtd) {
    work[ORIGIN_IDX] = qtd;
    work[QTD_IDX] = total;
    work[TOTAL_IDX] = moneyAfterQtd;
    moved.push(`Origem/Qtd/Valor Total realinhados: ${qtd} | ${total} | ${moneyAfterQtd}`);
  } else if ((!total || !isMoneyCell(total)) && moneyAfterQtd) {
    work[TOTAL_IDX] = moneyAfterQtd;
    moved.push(`Valor monetário movido para Valor Total: ${moneyAfterQtd}`);
  }

  // Normalização final obrigatória das colunas finais: Origem | Qtd | Valor Total.
  const finalOrigem = cleanCell(work[ORIGIN_IDX]);
  const finalQtd = cleanCell(work[QTD_IDX]);
  const finalTotal = cleanCell(work[TOTAL_IDX]);
  const extraMoneyIndex = work.findIndex((cell, index) => index > TOTAL_IDX && isMoneyCell(cell));
  if (!finalOrigem && isOriginCell(finalQtd)) {
    work[ORIGIN_IDX] = finalQtd;
    work[QTD_IDX] = INTEGER_RE.test(finalTotal) ? finalTotal : '';
    moved.push(`Qtd com texto movida para Origem: ${finalQtd}`);
  }
  if (extraMoneyIndex >= 0) {
    if (INTEGER_RE.test(finalTotal)) {
      work[QTD_IDX] = finalTotal;
      moved.push(`Valor Total numérico movido para Qtd: ${finalTotal}`);
    }
    work[TOTAL_IDX] = cleanCell(work[extraMoneyIndex]);
    moved.push(`Valor monetário extra movido para Valor Total: ${work[TOTAL_IDX]}`);
  } else if (!isMoneyCell(cleanCell(work[TOTAL_IDX]))) {
    const moneyAtOrAfterTotal = work.findIndex((cell, index) => index >= TOTAL_IDX && isMoneyCell(cell));
    if (moneyAtOrAfterTotal >= 0) {
      work[TOTAL_IDX] = cleanCell(work[moneyAtOrAfterTotal]);
      moved.push(`Valor monetário final movido para Valor Total: ${work[TOTAL_IDX]}`);
    }
  }

  if (isMoneyCell(cleanCell(work[QTD_IDX])) && !cleanCell(work[TOTAL_IDX])) {
    work[TOTAL_IDX] = work[QTD_IDX].trim();
    work[QTD_IDX] = '';
    moved.push(`Valor monetário movido de Qtd para Valor Total: ${work[TOTAL_IDX]}`);
  }

  if (work.length > REPORT_COLUMN_COUNT) {
    removedExtras = work.slice(REPORT_COLUMN_COUNT).filter(Boolean);
    work = work.slice(0, REPORT_COLUMN_COUNT);
  }

  const corrected = work.map(cleanCell);
  if (moved.length || removedExtras.length || corrected.length !== original.length || corrected.some((c, i) => c !== original[i])) {
    console.log('[pdf→xlsx] linha normalizada:', {
      original,
      corrigida: corrected,
      valoresMovidos: moved,
      colunasExtrasRemovidas: removedExtras,
    });
  }
  return corrected;
}

function normalizeReportRows(rows: string[][]): string[][] {
  const normalized = rows
    .map(normalizeRow)
    .filter((row): row is string[] => Boolean(row));
  console.log('[pdf→xlsx] normalização final:', {
    linhasOriginais: rows.length,
    linhasFinais: normalized.length,
    colunas: REPORT_COLUMN_COUNT,
  });
  return normalized;
}

function parseMoney(s: string): number {
  const m = (s ?? '').trim().match(MONEY_TOKEN_RE);
  if (!m) return 0;
  const raw = m[0].replace(/R\$\s?/, '').replace(/\./g, '').replace(',', '.');
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Build a real .xlsx Blob with a single "Detalhamento" sheet + optional "Resumo". */
function buildXlsxBlob(extracted: ExtractedPdf[]): Blob {
  const wb = XLSX.utils.book_new();

  // Merge all PDFs into one Detalhamento sheet, single header
  const merged: string[][] = [REPORT_HEADERS];
  let totalPages = 0;
  for (const ex of extracted) {
    totalPages += ex.pageCount;
    const norm = normalizeReportRows(ex.rows);
    for (const r of norm) {
      // Skip repeated header rows
      if (r.map(normalizeHeader).join('|') === HEADER_KEYS.join('|')) continue;
      if (r.length !== REPORT_COLUMN_COUNT) continue;
      merged.push(r);
    }
  }
  if (merged.length <= 1) throw new Error('NO_TABLES');

  const ws = XLSX.utils.aoa_to_sheet(merged);
  XLSX.utils.book_append_sheet(wb, ws, 'Detalhamento');

  // Resumo
  const dataRows = merged.slice(1);
  const totalValor = dataRows.reduce((acc, r) => acc + parseMoney(r[TOTAL_IDX] ?? ''), 0);
  const totalLaudos = dataRows.filter((r) => (r[3] ?? '').trim()).length;
  const resumo = [
    ['Métrica', 'Valor'],
    ['Total de páginas processadas', String(totalPages)],
    ['Total de linhas extraídas', String(dataRows.length)],
    ['Total de laudos', String(totalLaudos)],
    ['Soma do Valor Total', totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumo), 'Resumo');

  console.log('[pdf→xlsx] arquivo gerado:', {
    paginas: totalPages,
    linhas: dataRows.length,
    laudos: totalLaudos,
    valorTotal: totalValor,
  });

  const arr = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  return new Blob([arr], { type: XLSX_MIME });
}

interface ConverterToolPageProps {
  data: ConverterData;
}

// Map a converter's `fromFormat` string to accepted file extensions
const FORMAT_TO_EXTENSIONS: Record<string, string[]> = {
  PDF: ['.pdf'],
  'MÚLTIPLOS PDFS': ['.pdf'],
  XML: ['.xml'],
  EXCEL: ['.xlsx', '.xls'],
  WORD: ['.docx', '.doc'],
  CSV: ['.csv'],
  JSON: ['.json'],
  PNG: ['.png'],
  JPG: ['.jpg', '.jpeg'],
  'PNG/JPG': ['.png', '.jpg', '.jpeg'],
  'IMAGEM/PDF SCAN': ['.pdf', '.png', '.jpg', '.jpeg'],
};

const getAcceptedExtensions = (fromFormat: string): string[] => {
  const key = fromFormat.trim().toUpperCase();
  return FORMAT_TO_EXTENSIONS[key] ?? ['.pdf'];
};

/**
 * Try to open a PDF; returns `true` if password is required.
 * Does NOT attempt to crack — only detects encryption.
 */
async function isPdfPasswordProtected(file: File): Promise<boolean> {
  try {
    const buf = await file.arrayBuffer();
    const doc = await pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
    await doc.cleanup();
    return false;
  } catch (e: unknown) {
    if (e instanceof Error && e.name === 'PasswordException') return true;
    return false; // other read errors: let downstream handle
  }
}

/** Validate password by opening the PDF with it. */
async function tryUnlockPdf(file: File, password: string): Promise<boolean> {
  try {
    const buf = await file.arrayBuffer();
    const doc = await pdfjsLib.getDocument({ data: new Uint8Array(buf), password }).promise;
    await doc.cleanup();
    return true;
  } catch {
    return false;
  }
}

export const ConverterToolPage = ({ data }: ConverterToolPageProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const acceptedExtensions = getAcceptedExtensions(data.fromFormat);
  const acceptsPdf = acceptedExtensions.includes('.pdf');

  // Password prompt state for protected PDFs
  const [pwdPromptFile, setPwdPromptFile] = useState<File | null>(null);
  const [pwdQueue, setPwdQueue] = useState<File[]>([]);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdChecking, setPwdChecking] = useState(false);
  const pwdInputRef = useRef<HTMLInputElement>(null);
  const selectedFilesRef = useRef<File[]>([]);
  const passwordsRef = useRef<WeakMap<File, string>>(new WeakMap());

  // Result file ready for download
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string>('arquivo-convertido');

  // Compress-PDF only: level selector + stats
  const [compressionLevel, setCompressionLevel] = useState<CompressionLevel>('medium');
  const [compressStats, setCompressStats] = useState<
    { originalSize: number; finalSize: number; saved: number; pct: number } | null
  >(null);

  // Map output format → file extension for the download name
  const getOutExt = (toFormat: string): string => {
    const t = toFormat.trim().toUpperCase();
    if (t.includes('EXCEL')) return 'xlsx';
    if (t.includes('WORD')) return 'docx';
    if (t.includes('PDF')) return 'pdf';
    if (t.includes('XML')) return 'xml';
    if (t.includes('CSV')) return 'csv';
    if (t.includes('JSON')) return 'json';
    if (t.includes('PNG')) return 'png';
    if (t.includes('JPG') || t.includes('JPEG')) return 'jpg';
    if (t.includes('TEXTO')) return 'txt';
    return 'bin';
  };

  const isPdfToExcel =
    data.fromFormat.toUpperCase().includes('PDF') &&
    data.toFormat.toUpperCase().includes('EXCEL');
  const isExcelToCsv = data.slug === 'excel-para-csv';
  const isCompressPdf = data.slug === 'comprimir-pdf';
  const isMergePdf = data.slug === 'mesclar-pdf';
  const isPngToJpg = data.slug === 'png-para-jpg';
  const isJsonToExcel = data.slug === 'json-para-excel';
  const isXmlToExcel = data.slug === 'xml-para-excel';
  const isXmlToPdf = data.slug === 'xml-para-pdf';

  const setBlobAsDownload = (blob: Blob, baseName: string, ext: string) => {
    if (!blob || blob.size === 0) throw new Error('EMPTY_BLOB');
    if (ext === 'xlsx' && blob.type !== XLSX_MIME) throw new Error('BAD_MIME');
    const url = URL.createObjectURL(blob);
    setDownloadUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    setDownloadName(`${baseName}_convertido.${ext}`);
    console.log('[converter] arquivo gerado:', `${baseName}_convertido.${ext}`, 'bytes:', blob.size);
  };

  const MAX_FILE_MB = 20;
  const MAX_FILES = 20;

  type SpecKey = 'pdf' | 'xlsx' | 'xml' | 'json' | 'png';
  const ACCEPT_SPEC: Record<SpecKey, { exts: RegExp; magic?: (b: Uint8Array) => boolean; label: string }> = {
    pdf:  { exts: /\.pdf$/i,                  magic: (b) => b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46, label: 'PDF' },
    xlsx: { exts: /\.(xlsx|xls|xlsm)$/i,      magic: (b) => b[0] === 0x50 && b[1] === 0x4b, label: 'Excel' },
    xml:  { exts: /\.xml$/i,                  label: 'XML' },
    json: { exts: /\.json$/i,                 label: 'JSON' },
    png:  { exts: /\.png$/i,                  magic: (b) => b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47, label: 'PNG' },
  };

  const getExpectedTypes = (): SpecKey[] => {
    if (isPdfToExcel || isCompressPdf || isMergePdf) return ['pdf'];
    if (isExcelToCsv) return ['xlsx'];
    if (isXmlToExcel || isXmlToPdf) return ['xml'];
    if (isJsonToExcel) return ['json'];
    if (isPngToJpg) return ['png'];
    return [];
  };

  const validateFiles = async (files: File[]): Promise<string | null> => {
    if (files.length === 0) return 'Selecione ao menos um arquivo.';
    if (files.length > MAX_FILES) return `Máximo de ${MAX_FILES} arquivos por conversão.`;
    const expected = getExpectedTypes();
    const labels = expected.map((k) => ACCEPT_SPEC[k].label).join(' / ');
    for (const f of files) {
      if (f.size === 0) return `Arquivo vazio: ${f.name}`;
      if (f.size > MAX_FILE_MB * 1024 * 1024) return `"${f.name}" excede ${MAX_FILE_MB} MB.`;
      if (expected.length > 0) {
        const matchExt = expected.some((k) => ACCEPT_SPEC[k].exts.test(f.name));
        if (!matchExt) return `Tipo inválido em "${f.name}". Esperado: ${labels}.`;
        const head = new Uint8Array(await f.slice(0, 8).arrayBuffer());
        const magicOk = expected.some((k) => {
          const s = ACCEPT_SPEC[k];
          return s.exts.test(f.name) && (!s.magic || s.magic(head));
        });
        if (!magicOk) return `Conteúdo de "${f.name}" não corresponde a ${labels}.`;
      }
    }
    return null;
  };

  const runConversion = async (files: File[]) => {
    const validationError = await validateFiles(files);
    if (validationError) {
      toast({ title: 'Arquivo inválido', description: validationError, variant: 'destructive' });
      return;
    }
    setIsProcessing(true);
    setIsDone(false);
    try {
      const first = files[0];
      const baseName = first.name.replace(/\.[^.]+$/, '') || 'arquivo';


      if (isPdfToExcel) {
        const extracted: ExtractedPdf[] = [];
        for (const f of files) {
          if (!f.name.toLowerCase().endsWith('.pdf')) continue;
          const pwd = passwordsRef.current.get(f);
          const ex = await extractPdfRows(f, pwd);
          extracted.push(ex);
        }
        if (!extracted.some((e) => e.rows.length > 0)) throw new Error('NO_TABLES');
        const blob = buildXlsxBlob(extracted);
        setBlobAsDownload(blob, baseName, 'xlsx');
        toast({
          title: 'Conversão Concluída!',
          description: `Seu arquivo foi convertido de ${data.fromFormat} para ${data.toFormat} com sucesso.`,
        });
      } else if (isExcelToCsv) {
        const blob = await convertExcelToCsv(first);
        const sample = await blob.slice(0, 1024).text();
        if (!sample.replace(/^\uFEFF/, '').trim()) throw new Error('EMPTY_WORKBOOK');
        setBlobAsDownload(blob, baseName, 'csv');
        toast({
          title: 'Conversão Concluída!',
          description: 'CSV gerado em UTF-8, compatível com Excel e LibreOffice.',
        });
      } else if (isCompressPdf) {
        const { blob, originalSize, finalSize, ratio } = await compressPdf(first, compressionLevel);
        setBlobAsDownload(blob, baseName, 'pdf');
        const pct = Math.round(ratio * 100);
        setCompressStats({ originalSize, finalSize, saved: Math.max(0, originalSize - finalSize), pct });
        if (ratio < 0.05) {
          toast({
            title: 'PDF já otimizado',
            description: `Original: ${formatBytes(originalSize)} · Final: ${formatBytes(finalSize)} · Redução: ${pct}%. Este PDF já está otimizado e não pode ser comprimido significativamente.`,
          });
        } else {
          toast({
            title: 'PDF comprimido!',
            description: `Original: ${formatBytes(originalSize)} · Final: ${formatBytes(finalSize)} · Economia: ${pct}%.`,
          });
        }
      } else if (isMergePdf) {
        const pdfFiles = files.filter((f) => f.name.toLowerCase().endsWith('.pdf'));
        if (pdfFiles.length < 2) throw new Error('NEED_TWO_PDFS');
        const merged = await PDFDocument.create();
        for (const f of pdfFiles) {
          const bytes = new Uint8Array(await f.arrayBuffer());
          const src = await PDFDocument.load(bytes, { ignoreEncryption: false });
          const pages = await merged.copyPages(src, src.getPageIndices());
          pages.forEach((p) => merged.addPage(p));
        }
        const out = await merged.save();
        const blob = new Blob([out as BlobPart], { type: PDF_MIME });
        setBlobAsDownload(blob, baseName, 'pdf');
        toast({ title: 'PDFs mesclados!', description: `${pdfFiles.length} arquivos unidos com sucesso.` });
      } else if (isPngToJpg) {
        const imgFiles = files.filter((f) => /\.png$/i.test(f.name));
        if (imgFiles.length === 0) throw new Error('NO_PNG');
        const convertOne = (file: File): Promise<Blob> =>
          new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              const ctx = canvas.getContext('2d');
              if (!ctx) { URL.revokeObjectURL(url); reject(new Error('CANVAS_UNAVAILABLE')); return; }
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0);
              canvas.toBlob((b) => {
                URL.revokeObjectURL(url);
                b ? resolve(b) : reject(new Error('JPEG_ENCODE_FAILED'));
              }, 'image/jpeg', 0.92);
            };
            img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('IMG_LOAD_FAILED')); };
            img.src = url;
          });
        if (imgFiles.length === 1) {
          const blob = await convertOne(imgFiles[0]);
          setBlobAsDownload(blob, baseName, 'jpg');
        } else {
          const JSZip = (await import('jszip')).default;
          const zip = new JSZip();
          for (const f of imgFiles) {
            const b = await convertOne(f);
            zip.file(f.name.replace(/\.png$/i, '.jpg'), b);
          }
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const url = URL.createObjectURL(zipBlob);
          setDownloadUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
          setDownloadName(`${baseName}_convertido.zip`);
        }
        toast({ title: 'Conversão concluída!', description: `${imgFiles.length} imagem(ns) convertida(s) para JPG.` });
      } else if (isJsonToExcel) {
        const text = await first.text();
        let parsed: unknown;
        try { parsed = JSON.parse(text); } catch { throw new Error('INVALID_JSON'); }
        const rows = Array.isArray(parsed) ? parsed : [parsed];
        const ws = XLSX.utils.json_to_sheet(rows as Record<string, unknown>[]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Dados');
        const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
        const blob = new Blob([out], { type: XLSX_MIME });
        setBlobAsDownload(blob, baseName, 'xlsx');
        toast({ title: 'JSON convertido!', description: `${rows.length} registro(s) na planilha.` });
      } else if (isXmlToExcel) {
        const xmlFiles = files.filter((f) => /\.xml$/i.test(f.name));
        if (xmlFiles.length === 0) throw new Error('NO_XML');
        const allRows: Record<string, string>[] = [];
        for (const f of xmlFiles) {
          const text = await f.text();
          const doc = new DOMParser().parseFromString(text, 'application/xml');
          if (doc.querySelector('parsererror')) throw new Error('INVALID_XML');
          const row: Record<string, string> = { arquivo: f.name };
          doc.querySelectorAll('*').forEach((el) => {
            if (el.children.length === 0 && el.textContent?.trim()) {
              const key = el.tagName;
              if (!(key in row)) row[key] = el.textContent.trim();
            }
          });
          allRows.push(row);
        }
        const ws = XLSX.utils.json_to_sheet(allRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'XMLs');
        const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
        const blob = new Blob([out], { type: XLSX_MIME });
        setBlobAsDownload(blob, baseName, 'xlsx');
        toast({ title: 'XML(s) convertido(s)!', description: `${xmlFiles.length} arquivo(s) consolidado(s).` });
      } else if (isXmlToPdf) {
        const text = await first.text();
        const doc = new DOMParser().parseFromString(text, 'application/xml');
        if (doc.querySelector('parsererror')) throw new Error('INVALID_XML');
        const { jsPDF } = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;
        const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
        pdf.setFontSize(14);
        pdf.text(`Documento XML: ${first.name}`, 40, 40);
        const rows: [string, string][] = [];
        doc.querySelectorAll('*').forEach((el) => {
          if (el.children.length === 0 && el.textContent?.trim()) {
            rows.push([el.tagName, el.textContent.trim().slice(0, 200)]);
          }
        });
        autoTable(pdf, {
          head: [['Campo', 'Valor']],
          body: rows,
          startY: 60,
          styles: { fontSize: 9, cellPadding: 4 },
          headStyles: { fillColor: [22, 78, 99] },
        });
        const blob = pdf.output('blob');
        setBlobAsDownload(blob, baseName, 'pdf');
        toast({ title: 'XML convertido em PDF!', description: `${rows.length} campos renderizados.` });
      } else {
        throw new Error('CONVERTER_NOT_IMPLEMENTED');
      }

      // Track successful conversion for activity stats
      try {
        const { trackEvent } = await import('@/lib/analytics');
        await trackEvent('converter_used', {
          tool: data.slug,
          from: data.fromFormat,
          to: data.toFormat,
          files: files.length,
        });

      } catch { /* analytics best-effort */ }

      setIsDone(true);

    } catch (err: unknown) {
      console.error('[converter] erro:', err);
      const errorMessage = err instanceof Error ? err.message : '';
      const errMap: Record<string, string> = {
        NO_TABLES: 'Não foi possível extrair tabelas deste PDF.',
        EMPTY_WORKBOOK: 'A planilha está vazia ou não pôde ser lida.',
        INVALID_PDF_OUTPUT: 'O PDF de saída não pôde ser validado. Tente outro arquivo.',
        NEED_TWO_PDFS: 'Selecione pelo menos 2 arquivos PDF para mesclar.',
        NO_PNG: 'Selecione ao menos uma imagem PNG.',
        NO_XML: 'Selecione ao menos um arquivo XML.',
        INVALID_JSON: 'JSON inválido. Verifique a estrutura do arquivo.',
        INVALID_XML: 'XML inválido. Verifique a estrutura do arquivo.',
        IMG_LOAD_FAILED: 'Não foi possível carregar a imagem.',
        JPEG_ENCODE_FAILED: 'Falha ao gerar JPG.',
        CONVERTER_NOT_IMPLEMENTED: 'Este conversor ainda não está disponível.',
      };
      const msg = errMap[errorMessage] || 'Falha ao gerar o arquivo. Tente novamente.';
      toast({ title: 'Erro na conversão', description: msg, variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!downloadUrl) {
      toast({
        title: 'Arquivo não disponível',
        description: 'Envie um arquivo e processe novamente.',
        variant: 'destructive',
      });
      return;
    }
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleConvertAnother = () => {
    if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    setDownloadUrl(null);
    setIsDone(false);
    setCompressStats(null);
  };


  const handleFilesSelected = async (files: File[]) => {
    if (files.length === 0) return;
    selectedFilesRef.current = files;

    if (acceptsPdf) {
      // Detect any password-protected PDFs in the batch
      const locked: File[] = [];
      for (const f of files) {
        if (f.name.toLowerCase().endsWith('.pdf') && (await isPdfPasswordProtected(f))) {
          locked.push(f);
        }
      }
      if (locked.length > 0) {
        setPwdError(null);
        setPwdQueue(locked);
        setPwdPromptFile(locked[0]);
        return; // pause until user supplies password(s)
      }
    }

    runConversion(files);
  };

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pwdPromptFile) return;
    const pwd = pwdInputRef.current?.value ?? '';
    if (!pwd) return;
    setPwdChecking(true);
    const ok = await tryUnlockPdf(pwdPromptFile, pwd);
    setPwdChecking(false);
    if (!ok) {
      setPwdError('Senha incorreta.');
      return;
    }
    // Store password for this file so conversion can reopen it
    passwordsRef.current.set(pwdPromptFile, pwd);
    if (pwdInputRef.current) pwdInputRef.current.value = '';
    const remaining = pwdQueue.slice(1);
    setPwdQueue(remaining);
    setPwdError(null);
    if (remaining.length > 0) {
      setPwdPromptFile(remaining[0]);
    } else {
      setPwdPromptFile(null);
      runConversion(selectedFilesRef.current);
    }
  };

  const cancelPasswordPrompt = () => {
    setPwdPromptFile(null);
    setPwdQueue([]);
    setPwdError(null);
  };

  const IconComponent = () => {
    const props = { className: "w-12 h-12 text-cyan-400" };
    switch (data.iconType) {
      case 'pdf': return <FileText {...props} />;
      case 'excel': return <FileSpreadsheet {...props} />;
      case 'word': return <FileStack {...props} />;
      case 'xml': return <FileCode {...props} />;
      default: return <FileText {...props} />;
    }
  };

  return (
    <PremiumBackground>
      <Helmet>
        <title>{data.title} | FiscalOnne</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={`https://fiscalonne.online${data.slug}`} />
        <meta property="og:title" content={data.title} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:type" content="website" />
        
        {/* JSON-LD Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": data.title,
            "description": data.metaDescription,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "All",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "BRL"
            },
            "featureList": data.features.join(", ")
          })}
        </script>
      </Helmet>

      {/* Navigation Header */}
      <nav className="w-full border-b border-white/5 bg-black/20 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity group">
            <div className="relative w-8 h-8 rounded-lg bg-black border border-white/10 flex items-center justify-center">
              <Activity className="w-4 h-4 text-cyan-400" />
            </div>
            <h1 className="text-md font-bold text-white tracking-tight hidden sm:block">
              Fiscal<span className="text-cyan-500">Onne</span>
            </h1>
          </Link>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-slate-400 hover:text-white hover:bg-white/5 gap-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
      </nav>

      <PageHeader
        breadcrumbs={[
          { label: 'Conversores', to: '/conversores' },
          { label: data.title },
        ]}
        fallbackTo="/conversores"
        className="pt-4"
      />

      {/* Hero Section */}
      <section className="pt-4 pb-12 px-4 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge className="mb-4 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 hover:bg-cyan-500/20 transition-colors px-4 py-1">
              PRODUTIVIDADE FISCAL
            </Badge>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tighter uppercase italic">
              {data.h1}
            </h1>
            <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              {data.description}
            </p>
          </motion.div>

          {/* Tool Interaction Area */}
          <div className="max-w-2xl mx-auto">
            <AnimatePresence mode="wait">
              {!isDone ? (
                <motion.div
                  key="upload"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  {isCompressPdf && (
                    <div className="mb-4 p-4 rounded-xl bg-slate-900/60 border border-white/10">
                      <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">
                        Nível de compressão
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {([
                          { id: 'light',   label: 'Leve',    hint: '~200 DPI · qualidade alta' },
                          { id: 'medium',  label: 'Média',   hint: '~120 DPI · equilibrada' },
                          { id: 'extreme', label: 'Extrema', hint: '~90 DPI · menor tamanho' },
                        ] as const).map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            disabled={isProcessing}
                            onClick={() => setCompressionLevel(opt.id)}
                            className={cn(
                              'text-left rounded-lg border p-3 transition',
                              compressionLevel === opt.id
                                ? 'border-cyan-500/60 bg-cyan-500/10 text-white'
                                : 'border-white/10 bg-transparent text-slate-300 hover:border-white/20',
                            )}
                          >
                            <div className="font-bold text-sm uppercase tracking-wider">{opt.label}</div>
                            <div className="text-xs text-slate-400 mt-1">{opt.hint}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="p-1 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-2xl overflow-hidden shadow-2xl shadow-cyan-500/10">
                    <div className="bg-[#0A1118] rounded-[15px] overflow-hidden">
                      <FileUpload 
                        onFilesSelected={handleFilesSelected} 
                        isProcessing={isProcessing}
                        acceptedExtensions={acceptedExtensions}
                        formatLabel={data.fromFormat}
                      />
                    </div>
                  </div>
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500 font-mono uppercase tracking-widest">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-cyan-500" />
                      Protocolo Seguro
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Zap className="w-4 h-4 text-purple-500" />
                      Velocidade IA
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Lock className="w-4 h-4 text-cyan-500" />
                      Zero Cache
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-cyan-950/20 border-2 border-dashed border-cyan-500/50 rounded-2xl p-12 text-center"
                >
                  <div className="w-20 h-20 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/20">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2 uppercase italic tracking-tighter">Conversão Concluída</h3>
                  <p className="text-slate-400 mb-8">Seu arquivo está pronto para download em tempo recorde.</p>
                  {isCompressPdf && compressStats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 text-left">
                      <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Original</div>
                        <div className="text-white font-bold mt-1">{formatBytes(compressStats.originalSize)}</div>
                      </div>
                      <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Comprimido</div>
                        <div className="text-white font-bold mt-1">{formatBytes(compressStats.finalSize)}</div>
                      </div>
                      <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">Economia</div>
                        <div className="text-white font-bold mt-1">{formatBytes(compressStats.saved)}</div>
                      </div>
                      <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 p-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">Redução</div>
                        <div className="text-white font-bold mt-1">{compressStats.pct}%</div>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button
                      size="lg"
                      onClick={handleDownload}
                      className="w-full sm:w-auto gap-2 bg-white text-black hover:bg-slate-200 font-bold px-8"
                    >
                      <Download className="w-5 h-5" />
                      BAIXAR {data.toFormat}
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-full sm:w-auto border-white/10 text-white hover:bg-white/5"
                      onClick={handleConvertAnother}
                    >
                      CONVERTER OUTRO
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Info Sections */}
      <section className="py-20 px-4 border-t border-white/5 bg-black/20">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16">
          
          {/* How to use */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3 italic uppercase tracking-tighter">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                <LayoutGrid className="w-5 h-5 text-cyan-400" />
              </div>
              Procedimento Operacional
            </h2>
            <div className="space-y-6">
              {data.steps.map((step, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-900 border border-white/10 flex items-center justify-center font-mono text-cyan-400 text-sm">
                    {i + 1}
                  </div>
                  <p className="text-slate-400 pt-1 leading-relaxed">
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3 italic uppercase tracking-tighter">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                <Zap className="w-5 h-5 text-purple-400" />
              </div>
              Tecnologia FiscalOnne
            </h2>
            <div className="grid gap-4">
              {data.features.map((feature, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-slate-900/40 rounded-xl border border-white/5 shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                  <span className="text-slate-300 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 italic uppercase tracking-tighter">Dúvidas Frequentes</h2>
            <p className="text-slate-400">
              Esclarecimentos técnicos sobre a conversão de {data.fromFormat} para {data.toFormat}.
            </p>
          </div>
          
          <Accordion type="single" collapsible className="w-full">
            {data.faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/5">
                <AccordionTrigger className="text-left font-bold text-slate-200 hover:text-cyan-400 transition-colors py-6 no-underline uppercase tracking-tight text-sm">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-base leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 text-center">
        <div className="max-w-4xl mx-auto relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
          <div className="relative bg-[#0A1118] border border-white/5 rounded-[2rem] p-12 overflow-hidden">
            <h2 className="text-3xl font-black text-white mb-4 uppercase italic italic tracking-tighter">Ganhe horas de vida agora</h2>
            <p className="text-slate-400 mb-8 text-lg max-w-xl mx-auto leading-relaxed">
              O tempo que você gastaria manualmente nós resolvemos em segundos. Escalabilidade para sua gestão fiscal.
            </p>
            <Button 
              onClick={() => navigate('/app')}
              size="lg" 
              className="px-8 font-black h-14 bg-gradient-to-r from-cyan-500 to-purple-600 hover:scale-105 transition-all"
            >
              ACESSAR DASHBOARD COMPLETO
            </Button>
          </div>
        </div>
      </section>
      <Footer />

      {/* Password prompt for protected PDFs */}
      <Dialog open={!!pwdPromptFile} onOpenChange={(o) => { if (!o) cancelPasswordPrompt(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-cyan-500" />
              PDF protegido por senha
            </DialogTitle>
            <DialogDescription>
              Este PDF está protegido por senha. Informe a senha para continuar.
              {pwdPromptFile && (
                <span className="block mt-2 text-xs text-muted-foreground truncate">
                  Arquivo: {pwdPromptFile.name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPassword} className="space-y-3">
            <Input
              ref={pwdInputRef}
              type="password"
              autoFocus
              autoComplete="off"
              maxLength={256}
              placeholder="Digite a senha do PDF"
            />
            {pwdError && (
              <p className="text-sm text-rose-500 flex items-center gap-1.5">
                <AlertCircle className="w-4 h-4" /> {pwdError}
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              A senha é usada apenas no seu navegador e descartada após o uso. Não é armazenada nem registrada em logs.
            </p>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={cancelPasswordPrompt}>
                Cancelar
              </Button>
              <Button type="submit" disabled={pwdChecking}>
                {pwdChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continuar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </PremiumBackground>
  );
};
